import * as React from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useT } from "@/i18n/provider"
import { api, ApiError } from "@/lib/api"
import type { InvitePreview } from "@/lib/types"
import { useAuth } from "@/lib/auth"
import { setActiveWorkspaceId } from "@/lib/workspace"

/** Accepts a workspace invite link (`/invite/:token`).
 *
 * This route is reachable while signed out - unlike the rest of the app it sits
 * outside `Protected` in App.tsx, so it can send an unauthenticated visitor to
 * sign in or register first. It preserves the invite link across that detour via
 * `?redirect=`, which login.tsx reads back once auth completes (see there for
 * the other half of this handshake).
 *
 * Once a user is present, this calls `api.acceptInvite` at most once (guarded by
 * a ref, since the auth/token state can re-render this effect) and, on success,
 * makes the joined workspace active before routing into the app.
 */
export function AcceptInvitePage() {
  const t = useT()
  const { token } = useParams<{ token: string }>()
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = React.useState<string | null>(null)
  const [preview, setPreview] = React.useState<InvitePreview | null>(null)
  const attempted = React.useRef(false)

  // Public, token-gated context: which workspace, and who invited you (masked).
  React.useEffect(() => {
    if (!token) return
    api
      .invitePreview(token)
      .then(setPreview)
      .catch(() => {
        /* unknown/invalid token - the accept attempt below surfaces the real error */
      })
  }, [token])

  React.useEffect(() => {
    if (loading || !user || !token || attempted.current) return
    attempted.current = true
    api
      .acceptInvite(token)
      .then((workspace) => {
        setActiveWorkspaceId(workspace.id)
        navigate("/agents", { replace: true })
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : t("acceptInvite.genericError"))
      })
  }, [loading, user, token, navigate, t])

  if (!token) return <Navigate to="/chat" replace />

  // Not signed in yet: detour through login/register, then come straight back here.
  if (!loading && !user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(`/invite/${token}`)}`} replace />
  }

  return (
    <div className="bg-background flex min-h-full flex-col items-center justify-center p-6">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle>{preview?.workspace_name || t("acceptInvite.title")}</CardTitle>
          <CardDescription className="space-y-1">
            {preview?.invited_by && (
              <div>{t("acceptInvite.invitedBy", { who: preview.invited_by })}</div>
            )}
            <div>{error ?? (loading ? t("acceptInvite.checking") : t("acceptInvite.accepting"))}</div>
            {error && preview && (
              <div className="text-xs">
                {t("acceptInvite.sentTo", { email: preview.invited_email })}
              </div>
            )}
          </CardDescription>
        </CardHeader>
        {error && (
          <CardContent className="flex justify-center">
            <Button onClick={() => navigate("/agents", { replace: true })}>
              {t("acceptInvite.continue")}
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  )
}
